/**
 * Sentry / GlitchTip integration.
 * Uses @sentry/browser — compatible with both Sentry and GlitchTip.
 */

import type { AppUser } from './types/auth';

type SentryModule = typeof import('@sentry/browser');

let Sentry: SentryModule | null = null;

export async function initSentry(user?: AppUser | null): Promise<void> {
    const dsn = import.meta.env.VITE_SENTRY_DSN;
    if (!dsn) return;

    try {
        Sentry = await import('@sentry/browser');

        Sentry.init({
            dsn,
            environment: import.meta.env.MODE,
            tracesSampleRate: 0.1,
            replaysOnErrorSampleRate: 1.0,
            replaysSessionSampleRate: 0.1,
            integrations: [
                Sentry.replayIntegration({
                    maskAllText: false,
                    maskAllInputs: true,
                    blockAllMedia: false,
                }),
            ],
        });

        if (user) {
            Sentry.setUser({
                username: user.username,
                email: user.email,
            });
        }
    } catch (e: any) {
        console.warn('Sentry init failed:', (e instanceof Error ? e.message : String(e)));
    }
}

export function captureException(err: unknown): void {
    if (Sentry) {
        Sentry.captureException(err);
    }
}

export function addBreadcrumb(category: string, message: string, level: string = 'info'): void {
    if (Sentry) {
        Sentry.addBreadcrumb({ category, message, level: level as any });
    }
}
